import React from 'react'
import { useContext } from 'react'
import {
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Typography,
    Box,
    Stack,
    Chip,
    useTheme,
    useMediaQuery
} from "@mui/material"
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import UserContextP from "Interfaces/Context"

interface PropsInterface {
    order: any
    color: string
}

const TableProducts: React.FC<PropsInterface> = ({ order, color }) => {
    const context = useContext(UserContextP)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const priceProduct = (price: number, cantidad: number): number => Math.round((price * cantidad) * 100) / 100
    const products = order?.products ?? []

    return (
        <Grid item xs={12}>
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    borderRadius: 2.5,
                    border: '1px solid #e2e8f0',
                    width: '100%',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(17, 42, 86, 0.03)'
                }}
            >
                <Box
                    sx={{
                        p: 1.8,
                        bgcolor: color || '#112a56',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                            Productos
                        </Typography>
                    </Stack>
                    <Chip
                        label={`${products.length} ${products.length === 1 ? 'ítem' : 'ítems'}`}
                        size="small"
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            color: '#fff',
                            fontWeight: 700,
                            borderRadius: '50px',
                            fontSize: '11px',
                            height: 22
                        }}
                    />
                </Box>

                {isMobile ? (
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
                        {products.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                No hay productos en esta orden.
                            </Typography>
                        ) : (
                            products.map((item: any, index: number) => {
                                const line = priceProduct(item.price, item.cantidad)
                                const lineWithTax = Math.round(line * 1.13 * 100) / 100
                                return (
                                    <Paper
                                        key={index}
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            mb: 1.2,
                                            borderRadius: 2,
                                            border: '1px solid #e2e8f0',
                                            bgcolor: '#ffffff'
                                        }}
                                    >
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item xs={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Chip
                                                        label={`${item.cantidad}x`}
                                                        size="small"
                                                        sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, borderRadius: '50px' }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                                                        {item.id}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                    {item.ItemName}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#112a56' }}>
                                                        {(process.env.REACT_APP_CURRENCY ?? '$') + ' ' + context.separatorMiles(lineWithTax)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">con IVA</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Precio unitario: {(process.env.REACT_APP_CURRENCY ?? '$') + ' ' + context.separatorMiles(item.price)} • Línea sin IVA: {(process.env.REACT_APP_CURRENCY ?? '$') + ' ' + context.separatorMiles(line)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                )
                            })
                        )}
                    </Box>
                ) : (
                    <Table size="small" aria-label="productos">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Código</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cant.</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Descripción</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: 0.5 }}>P. Unitario (sin IVA)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: 0.5 }}>P. Línea (sin IVA)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: 0.5 }}>P. Línea (con IVA)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ py: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">No hay productos en esta orden.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                products.map((item: any, index: number) => {
                                    const line = priceProduct(item.price, item.cantidad)
                                    const lineWithTax = Math.round(line * 1.13 * 100) / 100
                                    return (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                '&:last-child td': { borderBottom: 0 },
                                                transition: 'background-color 0.15s ease',
                                                '&:hover': { bgcolor: '#f8fafc' }
                                            }}
                                        >
                                            <TableCell component="th" scope="row" sx={{ width: 140 }}>
                                                <Chip
                                                    label={item.id}
                                                    size="small"
                                                    sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 600, fontSize: '11px', borderRadius: '6px', height: 22 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center" sx={{ width: 80 }}>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        bgcolor: '#eff6ff',
                                                        color: '#1d4ed8',
                                                        fontWeight: 700,
                                                        fontSize: '12px',
                                                        px: 1.2,
                                                        py: 0.4,
                                                        borderRadius: '50px'
                                                    }}
                                                >
                                                    {item.cantidad}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 420, whiteSpace: 'normal', color: '#1e293b', fontWeight: 500 }}>
                                                {item.ItemName}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 500 }}>
                                                {(process.env.REACT_APP_CURRENCY ?? '$') + ' ' + context.separatorMiles(item.price)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 500 }}>
                                                {(process.env.REACT_APP_CURRENCY ?? '$') + ' ' + context.separatorMiles(line)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#112a56', fontWeight: 700 }}>
                                                {(process.env.REACT_APP_CURRENCY ?? '$') + ' ' + context.separatorMiles(lineWithTax)}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
        </Grid>
    )
}

export default TableProducts